import React, { useEffect, useState } from "react";
import { Button, ConfigProvider, Pagination, Spin, Table, Tag, message, Select, Row, Col, Modal, Typography, Space, Badge, Card, Tooltip, Dropdown, Menu } from "antd";
import { ReloadOutlined, DownloadOutlined, FileTextOutlined, CloseOutlined, WarningOutlined, EyeOutlined, MoreOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { MakeApiCall } from "../../../../apis";
import { WrapperInner } from "./innerStyle";
import axios from "axios";
import { ENDPOINT } from "../../../../config";

const { Title, Text, Paragraph } = Typography;

// Custom hook to get window size
const useWindowSize = () => {
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowSize;
};

const canViewErrorLogs = (record) => {
    if (!record) return false;
    const status = typeof record.status === "string" ? record.status.toLowerCase() : "";

    if (status === "processing" || status === "pending") return false;

    return (
        status === "failed" ||
        status === "error" ||
        status === "completed_with_warnings" ||
        status === "completed with warnings" ||
        record.has_errors === true
    );
};

const ImportManager = ({ list: propList, loading: propLoading, userData, filterData }) => {
    // True loading state (for fetches or refresh)
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState({}); // Track loading state per row

    // Table/page state
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    // Filter state
    const [filters, setFilters] = useState({
        type: null,
        status: null,
        source: null
    });

    // Error log modal state
    const [errorLogModal, setErrorLogModal] = useState({
        visible: false,
        importId: null,
        importData: null,
        errorLogs: [],
        loading: false
    });

    // Detail modal state for mobile view
    const [detailModal, setDetailModal] = useState({
        visible: false,
        record: null
    });

    // Get window size and determine if mobile
    const windowSize = useWindowSize();
    const isMobile = windowSize.width < 768;

    // Fetch import logs from API with filters
    const fetchImportLogs = async (curPage = page, curSize = pageSize, currentFilters = filters) => {
        setLoading(true);
        try {
            // Build query parameters
            const queryParams = new URLSearchParams();
            queryParams.append('page', curPage);
            queryParams.append('per_page', curSize);
            
            // Add filters if they exist
            if (currentFilters.type) {
                queryParams.append('type', currentFilters.type);
            }
            if (currentFilters.status) {
                queryParams.append('status', currentFilters.status);
            }
            if (currentFilters.source) {
                queryParams.append('source', currentFilters.source);
            }

            const response = await MakeApiCall(
                `data-logs/imports?${queryParams.toString()}`,
                "get",
                null,
                true,
                {},
                userData?.token
            );
            if (response?.status && response?.data?.records) {
                setData(response.data.records || []);
                setTotal(response.data.pagination?.total ?? 0);
            } else {
                setData([]);
                setTotal(0);
            }
        } catch (err) {
            setData([]);
            setTotal(0);
            message.error("Failed to fetch import logs.");
        }
        setLoading(false);
    };

    // Fetch data when filters, page, or pageSize changes
    useEffect(() => {
        fetchImportLogs(page, pageSize, filters);
        // eslint-disable-next-line
    }, [page, pageSize, userData?.token, filters]);

    // Handle filter change - automatically triggers fetch via useEffect
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        // Reset to first page when filters change
        setPage(1);
    };

    // Handle refresh action
    const handleRefresh = () => {
        message.loading('Refreshing import log...', 1);
        setTimeout(() => {
            fetchImportLogs(page, pageSize, filters);
            message.success('Import log refreshed successfully');
        }, 1000);
    };


   
    const handleDownload = async (importId, fileName = "downloaded-file") => {
        setActionLoading((prev) => ({ ...prev, [importId]: "download" }));
        try {
            const response = await axios({
                method: "get",
                url: `${ENDPOINT}data-logs/imports/${importId}/download`,
                responseType: "blob",
                headers: {
                    Authorization: `Bearer ${userData?.token}`,
                },
            });

            const contentType = response?.headers?.["content-type"] || "application/octet-stream";
            const contentDisposition = response?.headers?.["content-disposition"] || "";

            // Some APIs may send error JSON as blob; detect and show message.
            if (contentType.includes("application/json")) {
                const errorText = await response.data.text();
                try {
                    const parsed = JSON.parse(errorText);
                    message.error(parsed?.message || "Failed to download file");
                } catch (e) {
                    message.error("Failed to download file");
                }
                return;
            }

            const blob = response.data instanceof Blob
                ? response.data
                : new Blob([response.data], { type: contentType });

            let finalFileName = fileName;

            // Support filename*=UTF-8'' and filename=
            const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
            const basicMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
            if (utfMatch?.[1]) {
                finalFileName = decodeURIComponent(utfMatch[1]);
            } else if (basicMatch?.[1]) {
                finalFileName = basicMatch[1];
            }

            if (!/\.[a-z0-9]+$/i.test(finalFileName)) {
                if (contentType.includes("spreadsheetml")) finalFileName += ".xlsx";
                else if (contentType.includes("csv")) finalFileName += ".csv";
                else if (contentType.includes("zip")) finalFileName += ".zip";
            }

            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = finalFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            const errorMsg = error?.response?.data?.message || "Download failed";
            message.error(errorMsg);
            console.error("Download failed:", error);
        } finally {
            setActionLoading((prev) => ({ ...prev, [importId]: null }));
        }
    };

    
    // Handle error log download - Show in modal
    const handleErrorLog = async (importId, record) => {
        const target = record || data.find((r) => r.id === importId);
        if (!canViewErrorLogs(target)) {
            message.info("Error logs are available once processing completes.");
            return;
        }

        setActionLoading(prev => ({ ...prev, [importId]: 'error' }));
        setErrorLogModal(prev => ({ ...prev, visible: true, loading: true, importId }));
        
        try {
            const response = await MakeApiCall(
                `data-logs/imports/${importId}/error-log`,
                "get",
                null,
                true,
                {},
                userData?.token
            );

            if (response?.status && response?.data) {
                setErrorLogModal({
                    visible: true,
                    importId,
                    importData: response.data.import,
                    errorLogs: response.data.error_log || [],
                    pagination: response.data.pagination,
                    loading: false
                });
            } else {
                message.error('Failed to fetch error log');
                setErrorLogModal(prev => ({ ...prev, visible: false, loading: false }));
            }
        } catch (error) {
            console.error('Error log fetch error:', error);
            message.error(error?.response?.data?.message || 'Failed to fetch error log');
            setErrorLogModal(prev => ({ ...prev, visible: false, loading: false }));
        } finally {
            setActionLoading(prev => ({ ...prev, [importId]: null }));
        }
    };

    // Close error log modal
    const handleCloseErrorModal = () => {
        setErrorLogModal({
            visible: false,
            importId: null,
            importData: null,
            errorLogs: [],
            loading: false
        });
    };

    // Status color mapping
    const getStatusColor = (status) => {
        if (typeof status !== "string") return "default";
        const s = status.toLowerCase();
        if (s === "completed") return "success";
        if (s === "processing") return "processing";
        if (s === "completed_with_warnings" || s === "completed with warnings" || s === "warning") return "warning";
        if (s === "failed" || s === "error") return "error";
        if (s === "pending") return "default";
        return "default";
    };

    // Get status badge color for modal
    const getStatusBadgeColor = (status) => {
        if (typeof status !== "string") return "default";
        const s = status.toLowerCase();
        if (s === "completed") return "green";
        if (s === "processing") return "blue";
        if (s === "completed_with_warnings" || s === "completed with warnings") return "orange";
        if (s === "failed" || s === "error") return "red";
        return "default";
    };

    // Render detail modal for mobile view
    const renderDetailModal = () => {
        if (!detailModal.record) return null;
        
        const record = detailModal.record;
        const status = record.status ? record.status.toLowerCase() : "";
        const isLoading = actionLoading[record.id];
        
        return (
            <Modal
                title="Import Details"
                open={detailModal.visible}
                onCancel={() => setDetailModal({ visible: false, record: null })}
                footer={null}
                width={isMobile ? '95%' : 600}
                bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
                centered
            >
                <div className="import-detail-modal">
                    <div className="detail-section">
                        <div className="detail-row">
                            <span className="detail-label">File Name:</span>
                            <span className="detail-value fw-bold">{record.original_name || 'No name'}</span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">File Size:</span>
                            <span className="detail-value">{record.file_size || '-'}</span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Import Type:</span>
                            <span className="detail-value">
                                <Tag color="blue">{record.type_label || '-'}</Tag>
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Source:</span>
                            <span className="detail-value">{record.source_label || '-'}</span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Status:</span>
                            <span className="detail-value">
                                <Tag color={getStatusColor(record.status)}>
                                    {typeof record.status === "string" ? record.status.charAt(0).toUpperCase() + record.status.slice(1) : record.status}
                                </Tag>
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Uploaded At:</span>
                            <span className="detail-value">
                                {record.created_at
                                    ? dayjs.unix(record.created_at).format("DD MMM YYYY - hh:mm A")
                                    : '-'}
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Rows Processed:</span>
                            <span className="detail-value">
                                {record.total_rows === null ? '—' : 
                                 record.rows_processed === null ? record.total_rows : 
                                 `${record.rows_processed}/${record.total_rows}`}
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">File ID:</span>
                            <span className="detail-value">
                                <Text code>{record.file_id}</Text>
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Actions:</span>
                            <span className="detail-value">
                                <Space>
                                    {(status === 'processing' || status === "completed" || status === "completed_with_warnings" || status === "completed with warnings" || record.has_errors) && (
                                        <Button
                                            size="small"
                                            danger
                                            icon={<DownloadOutlined />}
                                            onClick={() => handleDownload(record.id, record.original_name)}
                                            loading={isLoading === 'download'}
                                        >
                                            Download
                                        </Button>
                                    )}
                                    {canViewErrorLogs(record) && (
                                        <Button
                                            size="small"
                                            type="primary"
                                            icon={<FileTextOutlined />}
                                            onClick={() => handleErrorLog(record.id, record)}
                                            loading={isLoading === 'error'}
                                        >
                                            Error logs
                                        </Button>
                                    )}
                                </Space>
                            </span>
                        </div>
                    </div>
                </div>
            </Modal>
        );
    };

    // Render card view for mobile
    const renderCardView = () => {
        if (loading) {
            return <div className="text-center py-5"><Spin /></div>;
        }

        if (data.length === 0) {
            return <div className="text-center py-5">No import logs found</div>;
        }

        return (
            <div className="import-cards-container">
                {data.map((record) => {
                    const status = record.status ? record.status.toLowerCase() : "";
                    const isLoading = actionLoading[record.id];
                    
  return (
                        <Card 
                            key={record.id}
                            className="mb-3"
                            style={{ 
                                borderRadius: '8px',
                                borderLeft: `2px solid ${getStatusColor(record.status) === 'success' ? '#52c41a' : 
                                           getStatusColor(record.status) === 'warning' ? '#faad14' :
                                           getStatusColor(record.status) === 'error' ? '#ff4d4f' : '#1890ff'}`,
                                backgroundColor: 'var(--app-surface)'
                            }}
                        >
                            <div className="card-content">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div className="fw-bolder fs-6 text-one">
                                        {record.original_name || 'No name'}
                                    </div>
                                    <div className="d-flex">
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={() => setDetailModal({ visible: true, record })}
                                            icon={<EyeOutlined />}
                                            className="me-1"
                                        />
                                        <Dropdown
                                            overlay={
                                                <Menu>
                                                    {(status === 'processing' || status === "completed" || status === "completed_with_warnings" || status === "completed with warnings" || record.has_errors) && (
                                                        <Menu.Item 
                                                            key="download"
                                                            icon={<DownloadOutlined />}
                                                            onClick={() => handleDownload(record.id, record.original_name)}
                                                            disabled={isLoading === 'download'}
                                                        >
                                                            Download File
                                                        </Menu.Item>
                                                    )}
                                                    {canViewErrorLogs(record) && (
                                                        <Menu.Item 
                                                            key="error"
                                                            icon={<FileTextOutlined />}
                                                            onClick={() => handleErrorLog(record.id, record)}
                                                            disabled={isLoading === 'error'}
                                                        >
                                                            View Error Logs
                                                        </Menu.Item>
                                                    )}
                                                </Menu>
                                            }
                                            trigger={['click']}
                                        >
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<MoreOutlined />}
                                            />
                                        </Dropdown>
                                    </div>
                                </div>
                                
                                {/* Main Fields - 5 Key Fields */}
                                <Row gutter={[8, 8]} className="mb-2">
                                    <Col span={12}>
                                        <div className="fs-7">
                                            <span className="text-muted">Type:</span>
                                            <span className="ms-2 fw-semibold">{record.type_label || '-'}</span>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className="fs-7">
                                            <span className="text-muted">Source:</span>
                                            <span className="ms-2 fw-semibold">{record.source_label || '-'}</span>
                                        </div>
                                    </Col>
                                </Row>
                                
                                <Row gutter={[8, 8]} className="mb-2">
                                    <Col span={12}>
                                        <div className="fs-7">
                                            <span className="text-muted">Status:</span>
                                            <span className="ms-2">
                                                <Tag color={getStatusColor(record.status)} size="small">
                                                    {typeof record.status === "string" ? record.status.charAt(0).toUpperCase() + record.status.slice(1) : record.status}
                                                </Tag>
                                            </span>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className="fs-7">
                                            <span className="text-muted">Rows:</span>
                                            <span className="ms-2 fw-semibold">
                                                {record.total_rows === null ? '—' : 
                                                 record.rows_processed === null ? record.total_rows : 
                                                 `${record.rows_processed}/${record.total_rows}`}
                                            </span>
                                        </div>
                                    </Col>
                                </Row>
                                
                                <div className="mb-2">
                                    <div className="fs-7">
                                        <span className="text-muted">Uploaded:</span>
                                        <span className="ms-2">
                                            {record.created_at
                                                ? dayjs.unix(record.created_at).format("DD MMM YYYY - hh:mm A")
                                                : '-'}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="mb-2">
                                    <div className="fs-7">
                                        <span className="text-muted">File ID:</span>
                                        <Tooltip title={record.file_id}>
                                            <span className="ms-2 text-primary">
                                                {record.file_id?.substring(0, 12)}...
                                            </span>
                                        </Tooltip>
                                    </div>
                                </div>
                                
                                {/* View Details Button */}
                                <Button 
                                    type="link" 
                                    className="p-0 mt-2"
                                    onClick={() => setDetailModal({ visible: true, record })}
                                >
                                    View all details <i className="ki-outline ki-arrow-right fs-6 ms-1"></i>
                                </Button>
                            </div>
                        </Card>
                    );
                })}
            </div>
        );
    };

    // Desktop table columns
    const columns = [
        {
            title: "File Name",
            dataIndex: "original_name",
            key: "original_name",
            render: (text, record) => (
    <div>
                    <div className="fw-bolder">{text || <span className="text-muted">No name</span>}</div>
                    <div className="text-muted small">{record.file_size ? `${record.file_size}` : ""}</div>
                </div>
            )
        },
        {
            title: "Import Type",
            dataIndex: "type_label",
            key: "type_label"
        },
        {
            title: "Source",
            dataIndex: "source_label",
            key: "source_label"
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status, record) => (
                <Tag color={getStatusColor(status)}>
                    {typeof status === "string" ? status.charAt(0).toUpperCase() + status.slice(1) : status}
                </Tag>
            )
        },
        {
            title: "Uploaded At",
            dataIndex: "created_at",
            key: "created_at",
            render: (created_at) =>
                created_at
                    ? dayjs.unix(created_at).format("DD MMM YYYY - hh:mm A")
                    : <span className="text-muted">—</span>
        },
        {
            title: "Rows Processed",
            key: "rows_processed",
            render: (_, record) => {
                if (record.total_rows === null) return "—";
                if (record.rows_processed === null) return record.total_rows;
                return `${record.rows_processed}/${record.total_rows}`;
            }
        },
        {
            title: "File ID",
            dataIndex: "file_id",
            key: "file_id",
            render: (file_id) => (
                <Tooltip title={file_id}>
                    <span className="text-primary">{file_id?.substring(0, 8)}...</span>
                </Tooltip>
            )
        },
        {
            title: "Action",
            key: "action",
            render: (_, record) => {
                const status = record.status ? record.status.toLowerCase() : "";
                const isLoading = actionLoading[record.id];
                return (
                    <div className="d-flex gap-2">
                        {(status === 'processing' || status === "completed" || status === "completed_with_warnings" || status === "completed with warnings" || record.has_errors) && (
                            <Button
                                color="danger" variant="filled"
                                className="fs-7 fw-bold"
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownload(record.id, record.original_name)}
                                loading={isLoading === 'download'}
                            >
                                Download
                            </Button>
                        )}
                        {canViewErrorLogs(record) && (
                            <Button
                                color="primary" variant="filled"
                                className="fs-7 fw-bold"
                                icon={<FileTextOutlined />}
                                onClick={() => handleErrorLog(record.id, record)}
                                loading={isLoading === 'error'}
                            >
                                Error logs
                            </Button>
                        )}
    </div>
  );
}
        }
    ];

    // CSS styles for mobile view
    const mobileStyles = `
        .import-detail-modal .detail-section {
            margin-top: 0px;
        }
        
        .import-detail-modal .detail-row {
            display: flex;
            padding: 12px 0;
            border-bottom: 1px solid var(--app-border);
        }
        
        .import-detail-modal .detail-row:last-child {
            border-bottom: none;
        }
        
        .import-detail-modal .detail-label {
            width: 110px;
            font-weight: 500;
            color: var(--app-text-muted);
            flex-shrink: 0;
        }
        
        .import-detail-modal .detail-value {
            flex: 1;
            color: var(--app-text);
            word-break: break-word;
        }
        
        .import-cards-container .text-one {
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            white-space: normal;
        }
        
        @media (max-width: 768px) {
            .ant-card-body {
                padding: 12px;
            }
            .filters .ant-row {
                margin-bottom: 10px;
            }
        }
    `;

    return (
        <WrapperInner>
            <div className="row gy-5 g-xl-5">
                <div className="col-xxl-12">
                    <div className="card card-xxl-stretch mb-5 mb-xl-8">
                        <div className="card-header border-bottom-dashed">
                            <h3 className="card-title align-items-start flex-column">
                                <span className="card-label fw-bold">Import Audit Log</span>
                                <span className="text-gray-500 mt-1 fw-semibold fs-7">
                                    Inbound files with source, user, results, and download.
                                </span>
                            </h3>
                            <div className="card-toolbar filters">
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={12}>
                                        <Select
                                            placeholder="All Types"
                                            className="min-w-150px"
                                            size="large"
                                            style={{ width: '100%' }}
                                            value={filters.type}
                                            onChange={(value) => handleFilterChange('type', value)}
                                            allowClear
                                            options={filterData?.types?.map(type => ({
                                                label: type.label,
                                                value: type.value
                                            })) || []}
                                        />
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Select
                                            placeholder="All Statuses"
                                            className="min-w-175px"
                                            size="large"
                                            style={{ width: '100%' }}
                                            value={filters.status}
                                            onChange={(value) => handleFilterChange('status', value)}
                                            allowClear
                                            options={filterData?.statuses?.map(status => ({
                                                label: status.label,
                                                value: status.value
                                            })) || []}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </div>
                        <div className="card-body py-2 filters">
                            <div className="table-responsive">
                                <Spin spinning={loading}>
                                    {isMobile ? (
                                        renderCardView()
                                    ) : (
                                        <Table
                                            scroll={{ x: "max-content" }}
                                            columns={columns}
                                            dataSource={data}
                                            rowKey="id"
                                            pagination={false}
                                            components={{
                                                table: (p) =>
                                                    <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...p} />,
                                                header: { row: (p) => <tr {...p} className="fw-bolder text-dark bg-cus" /> }
                                            }}
                                        />
                                    )}
                                </Spin>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row gy-5 g-xl-5">
                <div className="col-xxl-12">
                    <div className="card card-xxl-stretch mb-5 mb-xl-8">
                        <div className="card-body py-2">
                            <ConfigProvider theme={{ token: { colorPrimary: "#f45b68" } }}>
                                <Pagination
                                    className="mt-3"
                                    current={page}
                                    pageSize={pageSize}
                                    total={total}
                                    showSizeChanger
                                    responsive={true}
                                    simple={isMobile}
                                    onChange={(p, s) => {
                                        setPage(p);
                                        setPageSize(s);
                                    }}
                                    showTotal={(t, r) =>
                                        isMobile ? `Total: ${t}` : `${r[0]}-${r[1]} of ${t} items`
                                    }
                                />
                            </ConfigProvider>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Log Modal */}
            <Modal
                title={
                    <Space>
                        <WarningOutlined style={{ color: '#faad14' }} />
                        <span>Import Error Log</span>
                        {errorLogModal.importData && (
                            <Badge 
                                count={errorLogModal.importData.error_count} 
                                style={{ backgroundColor: '#ff4d4f' }}
                                overflowCount={999}
                            />
                        )}
                    </Space>
                }
                open={errorLogModal.visible}
                onCancel={handleCloseErrorModal}
                footer={false}
                width={isMobile ? '95%' : 800}
                centered
            >
                <Spin spinning={errorLogModal.loading}>
                    {errorLogModal.importData && (
                        <div style={{ marginBottom: 20 }}>
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <div style={{ 
                                    padding: 12, 
                                    background: 'var(--app-surface-alt)', 
                                    borderRadius: 8,
                                    border: '1px solid var(--app-border)'
                                }}>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Text type="secondary">File Name:</Text>
                                            <div>
                                                <Text strong>{errorLogModal.importData.original_name}</Text>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <Text type="secondary">Status:</Text>
                                            <div>
                                                <Tag color={getStatusColor(errorLogModal.importData.status)}>
                                                    {errorLogModal.importData.status?.replace(/_/g, ' ').toUpperCase()}
                                                </Tag>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row gutter={16} style={{ marginTop: 12 }}>
                                        <Col span={12}>
                                            <Text type="secondary">Error Count:</Text>
                                            <div>
                                                <Text strong style={{ color: '#f45b68' }}>
                                                    {errorLogModal.importData.error_count || 0}
                                                </Text>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <Text type="secondary">Has Errors:</Text>
                                            <div>
                                                <Text strong>{errorLogModal.importData.has_errors ? 'Yes' : 'No'}</Text>
                                            </div>
                                        </Col>
                                    </Row>
                                    {errorLogModal.importData.message && (
                                        <div style={{ marginTop: 12 }}>
                                            <Text type="secondary">Message:</Text>
                                            <div>
                                                <Text>{errorLogModal.importData.message}</Text>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <Title level={5}>Error Details</Title>
                                    {errorLogModal.errorLogs.length > 0 ? (
                                        <div style={{ 
                                            maxHeight: 400, 
                                            overflowY: 'auto',
                                            border: '1px solid var(--app-border)',
                                            borderRadius: 8,
                                            padding: 12
                                        }}>
                                            {errorLogModal.errorLogs.map((error, index) => (
                                                <div 
                                                    key={index} 
                                                    style={{ 
                                                        padding: '8px 0',
                                                        borderBottom: index < errorLogModal.errorLogs.length - 1 ? '1px solid var(--app-border)' : 'none',
                                                        display: 'flex',
                                                        alignItems: 'flex-start'
                                                    }}
                                                >
                                                    <div style={{ 
                                                        minWidth: 40, 
                                                        color: '#f45b68',
                                                        fontWeight: 'bold',
                                                        marginRight: 12
                                                    }}>
                                                        {index + 1}.
                                                    </div>
                                                    <div style={{ flex: 1, wordBreak: 'break-word' }}>
                                                        <Text>{error}</Text>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ 
                                            textAlign: 'center', 
                                            padding: 40,
                                            color: 'var(--app-text-muted)'
                                        }}>
                                            <FileTextOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                                            <div>No error logs available</div>
                                        </div>
                                    )}
                                </div>
                            </Space>
                        </div>
                    )}
                </Spin>
            </Modal>

            {/* Detail Modal for Mobile */}
            {renderDetailModal()}
            
            {/* Mobile Styles */}
            <style>{mobileStyles}</style>
        </WrapperInner>
    );
};

export default ImportManager;